import type {ReactNode} from 'react';
import {QueryClientProvider, type QueryClient} from '@tanstack/react-query';

import {TooltipProvider} from '@/components/ui/tooltip';

/**
 * The provider chrome every rendered tree needs, in one place.
 *
 * `main.tsx` and `src/test/render.tsx` both render this rather than each
 * assembling their own stack. They used to mirror each other by hand, which
 * meant the test harness satisfied its own dependencies: deleting the app's
 * `TooltipProvider` broke every tooltip in production while the whole suite
 * stayed green, because the harness had a copy. A provider hoisted for
 * everything below it to rely on has to be declared once, or nothing verifies
 * it exists.
 *
 * **What belongs here:** context every rendered tree needs identically. What
 * doesn't, and why — because the obvious next move is to pull the rest of
 * `main.tsx`'s wrapper stack in, and two of them must not come:
 *
 * - `RouterProvider` — the app mounts a real router, tests an in-memory one.
 *   Legitimately different, so it stays each caller's business.
 * - `ErrorBoundary` — deliberately app-only. Wrapping tests in one would catch
 *   thrown render errors and show a fallback, turning real failures into
 *   passing tests that render the wrong thing. Tests want the throw.
 * - `StrictMode` — app-only for now. Adding it here is defensible (it surfaces
 *   double-render bugs) but it is a behaviour change across the whole suite,
 *   not a mirroring fix; do it as its own change with the fallout triaged.
 */
export const AppProviders = ({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Radix tooltips need an ancestor provider. One for the whole app —
          mounting one per tooltip (the pre-DERP-229 pattern) meant a form with
          N hinted labels mounted N providers. */}
      <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
    </QueryClientProvider>
  );
};
